import {useRouteLoaderData} from '@remix-run/react'

import {type Access, type Action, type Entity, Role} from '#types'
import {type SerializeFrom} from '#types/utils'

import {type loader as rootLoader} from '../root'

function isUser(user: unknown): user is SerializeFrom<typeof rootLoader>['user'] {
  return Boolean(user && typeof user === 'object' && 'id' in user && typeof user.id === 'string')
}

export function useOptionalUser() {
  const data = useRouteLoaderData<typeof rootLoader>('root')

  if (!data || !isUser(data.user)) {
    return null
  }

  return data.user
}

export function useUser() {
  const maybeUser = useOptionalUser()
  if (!maybeUser) {
    throw new Error(
      'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
    )
  }
  return maybeUser
}

export type User = ReturnType<typeof useUser>

export type PermissionString = `${Action}:${Entity}` | `${Action}:${Entity}:${Access}`

export function parsePermissionString(permissionString: PermissionString) {
  const [action, entity, access] = permissionString.split(':') as [
    Action,
    Entity,
    Access | undefined,
  ]
  return {
    action,
    entity,
    access: access ? (access.split(',') as Access[]) : undefined,
  }
}

export function userHasPermission(user: Pick<User, 'roles'> | null, permission: PermissionString) {
  if (!user) return false
  const {action, entity, access} = parsePermissionString(permission)

  return user.roles.some(role =>
    role.permissions.some(
      p => p.entity === entity && p.action === action && (!access || access.includes(p.access)),
    ),
  )
}

export function userHasRole(user: Pick<User, 'roles'> | null, role: Role) {
  if (!user) return false
  return user.roles.some(r => (r.name as Role) === role)
}

export function isAdmin(user: User) {
  return userHasRole(user, Role.Admin)
}
